import styled from "styled-components";
import { useEffect, useRef, useCallback } from "react";
import colours from "../../lib/colours";
import {
  featureSource,
  featureTypes,
  mapAdditionalModes,
  mapModes,
  nvrToolModes,
  nvrTools,
  Patch,
  RevegetationArea,
  Tree,
  treeCmType,
  treeSize,
  treeType,
  validationFeatureCreationContext,
  validations,
} from "../../lib/types";
import {
  useDialogContext,
  useDrawingContext,
  useFeaturesContext,
  useFeaturesDispatchContext,
  useMapContext,
  useMapDialogContext,
  useMapMessagingContext,
  useMapModeContext,
  useNvrToolModeContext,
} from "../../context/ContextProvider";
import CompactTable, {
  areaHeaderStyle,
  tableCellNumberStyle,
  tableHeaderNumberStyle,
} from "../../components/organisms/CompactTable";
import {
  getArea,
  getAreaToPerimeterRatio,
  getFeatureSourcesCount,
  getLTBenchmarks,
  getOverlappingFeatures,
  isPolygonInvalid,
  mergePolygons,
} from "../../utils/featureUtils";
import Badge from "../../components/atoms/Badge";
import {
  cmTypeHelp,
  initialEditMessage,
  minimumAreaToPerimeterRatio,
  overlappingPolygonAfterEditMessage,
  overlappingPolygonHeading,
  pointEditMessage,
  polygonEditMessage,
  replaceFeatureTypeToken,
  sizesHelp,
} from "../../lib/constants";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/svg/delete.svg";
import { IState } from "../../context/featuresReducer";
import { FilledButtonAlt } from "../../components/atoms/FilledButton";
import { IProps as DialogProps } from "../../components/organisms/Dialog";
import { initialDialogProps } from "./DialogContainer";
import { initialDialogProps as initialMapDialogProps } from "./DialogMapContainer";
import TreeProperties from "./TreeProperties";
import GraphicsLayer from "esri/layers/GraphicsLayer";
import {
  commarizeSimple,
  replaceStringTokens,
  splitCamelCase,
} from "../../utils/otherUtils";
import Callout from "../../components/molecules/Callout";
import { Heading5, SubHeading } from "../../components/atoms/Text";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import Polygon from "@arcgis/core/geometry/Polygon";
import Switch from "../../components/molecules/Switch";
import mapConfig from "../../lib/mapConfig";
import DialogCallout from "../../components/other/DialogCallout";
import { ReactComponent as AlertIcon } from "../../assets/svg/alert.svg";
import { runValidations } from "../../utils/validations";
import validationMessages from "../../lib/validationMessages";

const esriEditTypesToDetect = ["move-stop", "reshape-stop", "vertex-remove"];

const Container = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const FlaggedTreesWarningContainer = styled.div`
  flex: 0;
`;

const FeaturesListContainer = styled.section`
  flex: 1;
  background-color: ${colours.white};
  overflow: auto;
  border-top: 1px solid ${colours.offWhite};
  display: flex;
  flex-direction: column;
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-block: 4px;
  padding-inline: 12px;
`;

const ControlButton = styled(FilledButtonAlt)`
  color: ${colours.th1l2};
  padding: 10px;
  &:hover {
    color: ${colours.th1};
    background-color: transparent;
  }
`;

const SourcesContainer = styled.div`
  overflow: auto;
  padding-bottom: 16px;
`;

const SourceContainer = styled.section`
  padding-inline: 12px;
`;

const TypeContainer = styled.section`
  margin-block: 10px;
`;

const BadgesContainer = styled.div`
  display: flex;
  > *:not(:first-child) {
    margin-left: 4px;
  }
`;

const TableIconButton = styled.button`
  border: none;
  display: flex;
  cursor: pointer;
  padding: 9px 6px;
  background: transparent;
  margin: auto;
`;

const tableIconStyle: any = {
  height: "13px",
  position: "relative",
  top: "2px",
  left: "-4px",
};

// Uploaded or Drawn sections
const SourceSection = ({
  source,
  showHeader,
  children,
}: {
  source: string;
  showHeader: boolean;
  children: any;
}) => {
  return (
    <SourceContainer>
      {showHeader && <SubHeading>{source + " features"}</SubHeading>}
      {children}
    </SourceContainer>
  );
};

// Sections for each feature type (Patches, trees...)
const TypeSection = ({
  typeName,
  children,
}: {
  typeName: string;
  children: any;
}) => {
  return (
    <TypeContainer>
      <Heading5>{typeName}</Heading5>
      {children}
    </TypeContainer>
  );
};

const patchHeaders = [
  "", // ID
  "Area",
  "", // Area units
  "", // Delete button
];

const treeHeaders = [
  "", // ID
  "Genus/Species",
  "Cm",
  "", // Badges(Cm type and size)
  "", // Edit button
  "", // Delete button
];

const revegetationAreaHeaders = [
  "", // ID
  "Area/Perimeter Ratio",
  "Area",
  "", // Area units
  "", // Delete button
];

const patchHeaderStyles = [null, areaHeaderStyle];
const treeHeaderStyles = [null, null, tableHeaderNumberStyle];
const revegetationAreaHeaderStyles = [
  null,
  tableHeaderNumberStyle,
  areaHeaderStyle,
];

interface IProps {
  drawnGraphicRef: any;
}

function FeaturesList({ drawnGraphicRef }: IProps) {
  const { nvrToolMode }: { nvrToolMode: nvrToolModes } =
    useNvrToolModeContext();
  const { map } = useMapContext();
  const state: IState = useFeaturesContext();
  const dispatch = useFeaturesDispatchContext();
  const {
    sketchVm,
    pointGraphicsLayer,
  }: {
    sketchVm: SketchViewModel;
    pointGraphicsLayer: GraphicsLayer;
  } = useDrawingContext();
  const {
    mapMode,
    setMapMode,
  }: {
    mapMode: mapModes;
    setMapMode: any;
  } = useMapModeContext();
  const { setDialogProps }: { setDialogProps: (props: DialogProps) => void } =
    useDialogContext();
  const {
    setMapDialogProps,
  }: { setMapDialogProps: (props: DialogProps) => void } =
    useMapDialogContext();
  const { mapMessage, setMapMessage, setMapMessageType } =
    useMapMessagingContext();

  const mapModeRef = useRef<any>();
  const editEventListener = useRef<any>();
  const deleteEventListener = useRef<any>(); // this is for the sketch tool, not delete buttons or the clear all button
  const instructionsResetTimeout = useRef<any>(); // detects when user has deselected something they were editing
  const updateTreeTimeout = useRef<any>(); // debounces consecutive tree edits
  const updatePolygonTimeout = useRef<any>(); // debounces polygon validity check and overlap detection

  const anyFlaggedTrees = state.treesList.some((x: string) => {
    return (state.features[x] as Tree).benchmarkCm === undefined;
  });

  const onEditClose = (previousMapMessage: string) => {
    setMapMessage(previousMapMessage);
    setMapDialogProps(initialMapDialogProps);
  };

  const clearAll = useCallback(() => {
    if (mapMode === mapAdditionalModes.edit) {
      setMapMode(undefined);
    }
    dispatch({
      type: "clear",
    });
  }, [mapMode, setMapMode, dispatch]);

  const getPolygonFeatureRow = (featureId: string, featureType: string) => {
    const feature: any = state.features[featureId];
    const onDelete = () => {
      if (Object.keys(state.features).length === 1) {
        clearAll(); // also exits edit mode
      } else {
        let dispatchType;
        if (featureType === featureTypes.patch) {
          dispatchType = "deletePatch";
        } else if (featureType === featureTypes.revegetationArea) {
          dispatchType = "deleteRevegetationArea";
        } else {
          throw new Error(
            `Unsupported featureType value ${featureType} when attempting to getPolygonFeatureRow`
          );
        }
        dispatch({
          type: dispatchType,
          payload: { id: featureId },
        });
      }
    };
    const deleteButton = (
      <TableIconButton title="delete feature" onClick={onDelete}>
        <DeleteIcon style={{ height: "16px" }}></DeleteIcon>
      </TableIconButton>
    );
    const rowData = [
      feature["id"],
      commarizeSimple(feature["area"]),
      "ha",
      deleteButton,
    ];
    const rowStyles = [
      null,
      tableCellNumberStyle,
      { width: "12px", paddingLeft: 0 },
      { width: "32px", padding: 0 },
    ];
    if (featureType === featureTypes.revegetationArea) {
      const apRatioWarning =
        feature["apRatio"] < 20 ? (
          <AlertIcon
            style={tableIconStyle}
            title="Does not meet the minimum area to perimeter ratio of 20"
          ></AlertIcon>
        ) : (
          <></>
        );
      rowData.splice(
        1,
        0,
        <>
          {apRatioWarning}
          {Math.floor(feature["apRatio"])}
        </>
      );
      rowStyles.splice(1, 0, tableCellNumberStyle);
    }
    return { rowData, rowStyles };
  };

  const getTreeRow = (featureId: string) => {
    const feature: any = state.features[featureId];
    const size = feature["size"];
    const cmType = feature["cmType"];

    const onEdit = () => {
      if (mapMessage) {
        setMapMessage(undefined);
      }
      if (drawnGraphicRef.current) {
        pointGraphicsLayer.remove(drawnGraphicRef.current);
        drawnGraphicRef.current = undefined;
      }
      setMapDialogProps({
        open: true,
        onClose: () => onEditClose(mapMessage),
        modal: false,
        transparentOverlay: true,
        showHeader: false,
        showFooter: false,
        children: (
          <TreeProperties
            tree={feature}
            onClose={() => onEditClose(mapMessage)}
            onSubmit={() => onEditClose(mapMessage)}
          />
        ),
      });
    };
    const onDelete = () => {
      if (Object.keys(state.features).length === 1) {
        clearAll(); // also exits edit mode
      } else {
        dispatch({
          type: "deleteTrees",
          payload: [featureId],
        });
      }
    };
    const badges = (
      <BadgesContainer>
        <Badge color={colours[cmType]} helpText={cmTypeHelp[cmType]}>
          {cmType}
        </Badge>
        {(nvrToolMode === nvrTools.removal ||
          (nvrToolMode === nvrTools.offset && size === treeSize.Lg)) && (
          <Badge color={colours[size]} helpText={sizesHelp[size]}>
            {size}
          </Badge>
        )}
      </BadgesContainer>
    );
    const editButton = (
      <TableIconButton title="Edit properties" onClick={onEdit}>
        <EditIcon style={{ height: "16px" }}></EditIcon>
      </TableIconButton>
    );
    const deleteButton = (
      <TableIconButton title="Delete feature" onClick={onDelete}>
        <DeleteIcon style={{ height: "16px" }}></DeleteIcon>
      </TableIconButton>
    );
    const rowData = [
      feature["id"],
      feature["benchmark"],
      String(feature["cm"]),
      badges,
      editButton,
      deleteButton,
    ];
    const rowStyles = [
      null,
      null,
      tableCellNumberStyle, // for cm value
    ];
    const highlight = !feature["benchmarkCm"];
    return { rowData, rowStyles, highlight };
  };

  const updateTreeSizeParameters = useCallback(
    async (tree: Tree, benchmarkData?: any) => {
      let vegCode, benchmark, benchmarkCm, size;
      if (benchmarkData && benchmarkData.ltBenchmarks) {
        vegCode = benchmarkData.evc;
        const benchmarks = benchmarkData.ltBenchmarks;
        const tableConfig =
          mapConfig.mapServiceConfig.tables.evcLargeTreeBenchmark;
        const benchmarkExistsInNewPosition = benchmarks
          .map((x: any) => x[tableConfig.speciesField])
          .includes(tree.benchmark);
        if (benchmarkExistsInNewPosition) {
          ({ benchmark, benchmarkCm, size } = tree);
        } else if (benchmarks.length === 1) {
          benchmark = benchmarks[0][tableConfig.speciesField];
          benchmarkCm = Number(benchmarks[0][tableConfig.benchmarkField]);
          const treeCIR =
            tree.cmType === treeCmType.CIR
              ? Number(tree.cm)
              : Number(tree.cm) * Math.PI;
          size = treeCIR > benchmarkCm ? treeSize.Lg : treeSize.Sm;
        }
      }
      tree.vegCode = vegCode;
      tree.benchmark = benchmark;
      tree.benchmarkCm = benchmarkCm;
      tree.size = size as any;
      return tree;
    },
    []
  );

  const restartEditingFeature = useCallback(
    (graphic: Graphic) => {
      const handler = pointGraphicsLayer.graphics.on("after-add", (event) => {
        if (event.item.attributes.id === graphic.attributes.id) {
          sketchVm.update(event.item);
          handler.remove();
        }
      });
    },
    [sketchVm, pointGraphicsLayer]
  );

  const debouncedTreeUpdate = (fn: any) => {
    clearTimeout(updateTreeTimeout.current);
    updateTreeTimeout.current = setTimeout(() => fn(), 500);
  };

  const debouncedPolygonUpdate = (fn: any) => {
    clearTimeout(updatePolygonTimeout.current);
    updatePolygonTimeout.current = setTimeout(() => fn(), 500);
  };

  const updatePatch = useCallback(
    async (graphic: Graphic) => {
      const feature = { ...state.features[graphic.attributes.id] } as Patch;
      feature.geom = graphic.geometry.toJSON();
      feature.area = await getArea(graphic.geometry as Polygon);
      dispatch({
        type: "updatePatch",
        payload: feature,
      });
      restartEditingFeature(graphic);
    },
    [dispatch, state.features, restartEditingFeature]
  );

  const updateTree = useCallback(
    async (graphic: Graphic, newTreeType?: treeType, benchmarkData?: any) => {
      let feature = { ...state.features[graphic.attributes.id] } as Tree;
      feature = await updateTreeSizeParameters(feature, benchmarkData);
      feature.geom = graphic.geometry.toJSON();
      if (newTreeType) {
        feature.type = newTreeType;
      }
      dispatch({
        type: "updateTree",
        payload: feature,
      });
      restartEditingFeature(graphic);
    },
    [dispatch, state.features, restartEditingFeature, updateTreeSizeParameters]
  );

  const updateRevegetationArea = useCallback(
    async (graphic: Graphic, apRatio: number) => {
      const feature = {
        ...state.features[graphic.attributes.id],
      } as RevegetationArea;
      feature.geom = graphic.geometry.toJSON();
      feature.area = await getArea(graphic.geometry as Polygon);
      feature.apRatio = apRatio;

      dispatch({
        type: "updateRevegetationArea",
        payload: feature,
      });
      restartEditingFeature(graphic);
    },
    [dispatch, state.features, restartEditingFeature]
  );

  const onDiscard = useCallback(
    (graphic: Graphic) => {
      const feature = { ...state.features[graphic.attributes.id] };
      const GeometryConstructor = graphic.attributes.type ? Polygon : Point;
      graphic.geometry = new GeometryConstructor(feature.geom);
      setDialogProps(initialDialogProps);
      sketchVm.complete();
      sketchVm.update(
        (pointGraphicsLayer.graphics as any).items.filter(
          (x: Graphic) => x.attributes.id === graphic.attributes.id
        )
      );
    },
    [state.features, sketchVm, pointGraphicsLayer, setDialogProps]
  );

  const handleCriticalValidationFailure = useCallback(
    (graphic: Graphic, messageContent: any, messageTitle: string) => {
      setDialogProps({
        open: true,
        onClose: () => onDiscard(graphic),
        heading: messageTitle,
        children: (
          <DialogCallout
            calloutConfigs={[
              {
                type: "warning",
                content: messageContent,
              },
            ]}
          ></DialogCallout>
        ),
      });
    },
    [setDialogProps, onDiscard]
  );

  const handleNonCriticalValidationFailure = useCallback(
    (messages: any[], headings?: Array<string | undefined>) => {
      const heading =
        headings && headings.length === 1 && headings[0]
          ? headings[0]
          : "Warning";
      const calloutConfigs = messages.map((x) => ({
        type: "info",
        content: x,
      }));
      setDialogProps({
        open: true,
        onClose: () => {
          setDialogProps(initialDialogProps);
        },
        heading: calloutConfigs.length === 1 ? heading : "Warnings",
        children: (
          <DialogCallout calloutConfigs={calloutConfigs}></DialogCallout>
        ),
      });
    },
    [setDialogProps]
  );

  const onMerge = useCallback(
    async (
      featureType: string,
      polygonIds: string[],
      newGeom: Polygon,
      featSource: featureSource,
      editedGraphicId: string
    ) => {
      let dispatchType;
      if (featureType === featureTypes.patch) {
        dispatchType = "mergePatches";
      } else if (featureType === featureTypes.revegetationArea) {
        dispatchType = "mergeRevegetationAreas";
      } else {
        throw new Error(
          `Unsupported featureType value ${featureType} when attempting to merge polygons`
        );
      }
      const mergedFeature: any = {
        source: featSource,
        geom: newGeom.toJSON(),
        area: await getArea(newGeom as Polygon),
      };
      if (featureType === featureTypes.revegetationArea) {
        mergedFeature.apRatio = await getAreaToPerimeterRatio(
          newGeom as Polygon
        );
      }
      const payload: any = {
        polygonIds: polygonIds.concat(editedGraphicId),
        mergedFeature: mergedFeature,
      };
      if (featureType === featureTypes.patch) {
        payload.removedPolygonGeometry = new Polygon(
          state.features[editedGraphicId].geom
        );
      }

      dispatch({
        type: dispatchType,
        payload: payload,
      });
      setDialogProps(initialDialogProps);
      sketchVm.complete();

      if (
        mergedFeature.apRatio !== undefined &&
        mergedFeature.apRatio < minimumAreaToPerimeterRatio
      ) {
        handleNonCriticalValidationFailure(
          [validationMessages.apRatio.content()],
          [validationMessages.apRatio.title]
        );
      }
    },
    [
      dispatch,
      state.features,
      sketchVm,
      setDialogProps,
      handleNonCriticalValidationFailure,
    ]
  );

  const getNonCriticalPatchInfo = useCallback(
    async (graphic: Graphic) => {
      const polygon = graphic.geometry as Polygon;
      const validationsToRun = [];
      if (nvrToolMode === nvrTools.removal) {
        validationsToRun.push(validations.msaConservation);
        validationsToRun.push(validations.msa12Precincts);
      }
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.wetlands);
        validationsToRun.push(validations.parcelBoundaryProximity);
      }
      const nonCriticalValidations = await runValidations(
        validationsToRun,
        polygon,
        featureTypes.patch,
        map,
        state
      );
      return nonCriticalValidations.filter((x) => x.fail);
    },
    [nvrToolMode, map, state]
  );

  const getNonCriticalTreeInfo = useCallback(
    async (graphic: Graphic) => {
      const point = graphic.geometry as Point;
      const validationsToRun = [];
      if (nvrToolMode === nvrTools.removal) {
        validationsToRun.push(validations.msaConservation);
        validationsToRun.push(validations.msa12Precincts);
      }
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.parcelBoundaryProximity);
      }
      const nonCriticalValidations = await runValidations(
        validationsToRun,
        point,
        featureTypes.tree,
        map,
        state
      );
      return nonCriticalValidations.filter((x) => x.fail);
    },
    [nvrToolMode, map, state]
  );

  const getNonCriticalRevegetationInfo = useCallback(
    async (graphic: Graphic, apRatio?: number) => {
      const polygon = graphic.geometry as Polygon;
      const previousFeature = {
        ...state.features[graphic.attributes.id],
      } as RevegetationArea;
      const previousApRatio = previousFeature.apRatio;

      const validationsToRun = [];
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.parcelBoundaryProximity);
      }
      if (
        apRatio &&
        apRatio.toFixed(3) !== previousApRatio.toFixed(3) &&
        apRatio < minimumAreaToPerimeterRatio
      ) {
        validationsToRun.push(validations.apRatio);
      }
      const nonCriticalValidations = await runValidations(
        validationsToRun,
        polygon,
        featureTypes.revegetationArea,
        map,
        state,
        undefined,
        { apRatio }
      );
      return nonCriticalValidations.filter((x) => x.fail);
    },
    [nvrToolMode, map, state]
  );

  const handleInvalidPolygonAfterMerge = useCallback(
    (graphic: Graphic, featureType: featureTypes) => {
      handleCriticalValidationFailure(
        graphic,
        validationMessages.invalidPolygon.getInvalidAfterMergeMessage(
          featureType
        ),
        validationMessages.invalidPolygon.getMessageTitle(featureType)
      );
    },
    [handleCriticalValidationFailure]
  );

  const handleValidOverlap = useCallback(
    async (
      graphic: Graphic,
      featureType: string,
      overlappingPolygonIds: string[],
      newGeom: Polygon
    ) => {
      const featSource = state.features[graphic.attributes.id].source;
      const pluralSuffix =
        featureType === featureTypes.revegetationArea ? "s" : "es";
      const messageEnd =
        overlappingPolygonIds.length === 1
          ? ` ${overlappingPolygonIds[0]}.`
          : `${pluralSuffix} ${overlappingPolygonIds.join(", ")}.`;

      let calloutConfigs: any[] = [
        {
          type: "warning",
          content:
            replaceStringTokens(
              overlappingPolygonAfterEditMessage,
              replaceFeatureTypeToken,
              splitCamelCase(featureType)
            ) + messageEnd,
        },
      ];

      if (featureType === featureTypes.patch) {
        const info = await getNonCriticalPatchInfo(graphic);
        calloutConfigs = calloutConfigs.concat(
          info.map((x) => ({
            type: "info",
            content: x.messageContent,
          }))
        );
      }

      if (featureType === featureTypes.revegetationArea) {
        const info = await getNonCriticalRevegetationInfo(graphic);
        calloutConfigs = calloutConfigs.concat(
          info.map((x) => ({
            type: "info",
            content: x.messageContent,
          }))
        );
      }

      setDialogProps({
        open: true,
        onClose: () => onDiscard(graphic),
        heading: replaceStringTokens(
          overlappingPolygonHeading,
          replaceFeatureTypeToken,
          splitCamelCase(featureType)
        ),
        children: (
          <DialogCallout calloutConfigs={calloutConfigs}></DialogCallout>
        ),
        additionalButtons: [
          {
            text: `Merge overlapping ${splitCamelCase(
              featureType
            )}${pluralSuffix}`,
            onClickHandler: () =>
              onMerge(
                featureType,
                overlappingPolygonIds,
                newGeom,
                featSource,
                graphic.attributes.id
              ),
          },
        ],
        closeButtonText: "Discard edit",
        showXButton: false,
      });
    },
    [
      setDialogProps,
      getNonCriticalPatchInfo,
      getNonCriticalRevegetationInfo,
      onDiscard,
      onMerge,
      state.features,
    ]
  );

  const handleOverlappingPolygon = useCallback(
    (
      graphic: Graphic,
      featureType: featureTypes,
      overlappingPolygonIds: string[]
    ) => {
      const toMerge = [graphic.geometry as Polygon].concat(
        overlappingPolygonIds.map((x) => state.features[x].geom as Polygon)
      );
      const newGeom = mergePolygons(toMerge) as Polygon;
      if (isPolygonInvalid(newGeom)) {
        handleInvalidPolygonAfterMerge(graphic, featureType);
      } else {
        handleValidOverlap(
          graphic,
          featureType,
          overlappingPolygonIds,
          newGeom
        );
      }
    },
    [state, handleInvalidPolygonAfterMerge, handleValidOverlap]
  );

  const handleEditedPatch = useCallback(
    async (graphic: Graphic) => {
      const polygon = graphic.geometry as Polygon;
      let supportingValidationData = {};
      const validationsToRun: validations[] = [
        validations.invalidPolygon,
        validations.msaLevyArea,
        validations.outsideSupportingRasters,
      ];
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.overlappingTypes);
        validationsToRun.push(validations.treesBelowDbhThreshold);
        supportingValidationData = {
          otherFeatureType: featureTypes.revegetationArea,
          otherGeoms: state.revegetationAreasList.map(
            (x: string) => state.features[x].geom
          ),
        };
      }
      const criticalValidations = await runValidations(
        validationsToRun,
        polygon,
        featureTypes.patch,
        map,
        state,
        validationFeatureCreationContext.edit,
        { nvrToolMode, ...supportingValidationData }
      );
      const failedCriticalValidations = criticalValidations.filter(
        (x) => x.fail
      );
      if (failedCriticalValidations.length) {
        const { messageContent, messageTitle } = failedCriticalValidations[0];
        handleCriticalValidationFailure(
          graphic,
          messageContent,
          messageTitle || "Warning"
        );
        return;
      }

      const currentPatches = state.patchesList
        .filter((x: string) => x !== graphic.attributes.id)
        .map((x: string) => state.features[x]) as Patch[];

      const overlappingPatches = getOverlappingFeatures(
        polygon,
        currentPatches
      );
      if (overlappingPatches.length) {
        handleOverlappingPolygon(
          graphic,
          featureTypes.patch,
          overlappingPatches
        );
      } else {
        updatePatch(graphic);
        const info = await getNonCriticalPatchInfo(graphic);
        if (info.length) {
          handleNonCriticalValidationFailure(
            info.map((x) => x.messageContent),
            info.map((x) => x.messageTitle)
          );
        }
      }
    },
    [
      updatePatch,
      handleOverlappingPolygon,
      handleCriticalValidationFailure,
      handleNonCriticalValidationFailure,
      getNonCriticalPatchInfo,
      nvrToolMode,
      state,
      map,
    ]
  );

  const handleEditedTree = useCallback(
    async (graphic: Graphic) => {
      const point = graphic.geometry as Point;
      const benchmarkData = await getLTBenchmarks(point, map);
      const overlappingPatches = getOverlappingFeatures(
        graphic.geometry as Point,
        state.patchesList.map((x: string) => state.features[x] as Patch)
      );
      const tree = { ...state.features[graphic.attributes.id] } as Tree;

      const validationsToRun: validations[] = [
        validations.msaLevyArea,
        validations.outsideSupportingRasters,
      ];
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.treesBelowDbhThreshold);
      }
      const criticalValidations = await runValidations(
        validationsToRun,
        point,
        featureTypes.tree,
        map,
        state,
        validationFeatureCreationContext.edit,
        { tree, benchmarkData, overlappingPatches, nvrToolMode }
      );
      const failedCriticalValidations = criticalValidations.filter(
        (x) => x.fail
      );
      if (failedCriticalValidations.length) {
        const { messageContent, messageTitle } = failedCriticalValidations[0];
        handleCriticalValidationFailure(
          graphic,
          messageContent,
          messageTitle || "Warning"
        );
        return;
      }

      const newTreeType = overlappingPatches.length
        ? treeType.Patch
        : treeType.Scattered;
      updateTree(graphic, newTreeType, benchmarkData);
      const info = await getNonCriticalTreeInfo(graphic);
      if (info.length) {
        handleNonCriticalValidationFailure(
          info.map((x) => x.messageContent),
          info.map((x) => x.messageTitle)
        );
      }
    },
    [
      updateTree,
      state,
      map,
      handleCriticalValidationFailure,
      handleNonCriticalValidationFailure,
      getNonCriticalTreeInfo,
      nvrToolMode,
    ]
  );

  const handleEditedRevegetationArea = useCallback(
    async (graphic: Graphic) => {
      const polygon = graphic.geometry as Polygon;
      let supportingValidationData = {};
      const validationsToRun: validations[] = [
        validations.invalidPolygon,
        validations.msaLevyArea,
        validations.outsideSupportingRasters,
      ];
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.overlappingTypes);
        validationsToRun.push(validations.wetlands);
        validationsToRun.push(validations.nonWoodyEvc);
        supportingValidationData = {
          otherFeatureType: featureTypes.patch,
          otherGeoms: state.patchesList.map(
            (x: string) => state.features[x].geom
          ),
        };
      }
      const criticalValidations = await runValidations(
        validationsToRun,
        polygon,
        featureTypes.revegetationArea,
        map,
        state,
        validationFeatureCreationContext.edit,
        { nvrToolMode, ...supportingValidationData }
      );
      const failedCriticalValidations = criticalValidations.filter(
        (x) => x.fail
      );
      if (failedCriticalValidations.length) {
        const { messageContent, messageTitle } = failedCriticalValidations[0];
        handleCriticalValidationFailure(
          graphic,
          messageContent,
          messageTitle || "Warning"
        );
        return;
      }

      const currentRevegetationAreas = state.revegetationAreasList
        .filter((x: string) => x !== graphic.attributes.id)
        .map((x: string) => state.features[x]) as RevegetationArea[];

      const overlappingRevegetationAreas = getOverlappingFeatures(
        polygon,
        currentRevegetationAreas
      );

      if (overlappingRevegetationAreas.length) {
        handleOverlappingPolygon(
          graphic,
          featureTypes.revegetationArea,
          overlappingRevegetationAreas
        );
      } else {
        const apRatio = await getAreaToPerimeterRatio(
          graphic.geometry as Polygon
        );
        updateRevegetationArea(graphic, apRatio);
        const info = await getNonCriticalRevegetationInfo(graphic, apRatio);
        if (info.length) {
          handleNonCriticalValidationFailure(
            info.map((x) => x.messageContent),
            info.map((x) => x.messageTitle)
          );
        }
      }
    },
    [
      updateRevegetationArea,
      handleOverlappingPolygon,
      handleCriticalValidationFailure,
      handleNonCriticalValidationFailure,
      getNonCriticalRevegetationInfo,
      nvrToolMode,
      state,
      map,
    ]
  );

  const handleEditedFeatures = useCallback(
    (graphics: Graphic[]) => {
      if (graphics.length > 1) {
        console.warn("Not implemented; can not handle multiple edits");
      } else {
        const graphic = graphics[0];
        if (graphic.attributes.type === featureTypes.patch) {
          debouncedPolygonUpdate(() => handleEditedPatch(graphic));
        } else if (graphic.attributes.type === featureTypes.revegetationArea) {
          debouncedPolygonUpdate(() => handleEditedRevegetationArea(graphic));
        } else {
          debouncedTreeUpdate(() => handleEditedTree(graphic));
        }
      }
    },
    [handleEditedPatch, handleEditedTree, handleEditedRevegetationArea]
  );

  const onEditSwitchChange = (checked: boolean) => {
    if (checked) {
      sketchVm.updateOnGraphicClick = true;
      setMapMode(mapAdditionalModes.edit);
      setTimeout(() => {
        setMapMessage(initialEditMessage);
        setMapMessageType("action");
      }, 100);
    } else {
      sketchVm.updateOnGraphicClick = false;
      setMapMode(undefined);
      setMapMessage(undefined);
      setMapMessageType(undefined);
    }
  };

  // feature edit events
  useEffect(() => {
    if (!sketchVm) return;
    if (editEventListener.current) {
      editEventListener.current.remove();
    }
    editEventListener.current = sketchVm.on(
      "update" as any,
      ({
        graphics,
        state: eventState,
        toolEventInfo,
      }: {
        graphics: Graphic[];
        state: string;
        toolEventInfo: any;
      }) => {
        if (eventState === "start") {
          if (instructionsResetTimeout.current) {
            clearTimeout(instructionsResetTimeout.current);
          }
          setMapMessage(
            [featureTypes.patch, featureTypes.revegetationArea].indexOf(
              graphics[0].attributes.type
            ) !== -1
              ? polygonEditMessage
              : pointEditMessage
          );
        }
        if (eventState === "active") {
          clearTimeout(updatePolygonTimeout.current);
          clearTimeout(updateTreeTimeout.current);
        }
        if (eventState === "complete") {
          instructionsResetTimeout.current = setTimeout(() => {
            setMapMessage(initialEditMessage);
          }, 300);
        }
        if (
          toolEventInfo &&
          toolEventInfo.type &&
          esriEditTypesToDetect.includes(toolEventInfo.type)
        ) {
          handleEditedFeatures(graphics);
        }
      }
    );
    return () => {
      if (editEventListener.current) editEventListener.current.remove();
    };
  }, [
    sketchVm,
    editEventListener,
    handleEditedFeatures,
    instructionsResetTimeout,
    mapModeRef,
    setMapMessage,
    updatePolygonTimeout,
    updateTreeTimeout,
  ]);

  // feature delete events
  useEffect(() => {
    if (!sketchVm) return;
    if (deleteEventListener.current) {
      deleteEventListener.current.remove();
    }
    deleteEventListener.current = sketchVm.on(
      "delete" as any,
      ({ graphics }: { graphics: Graphic[] }) => {
        if (Object.keys(state.features).length === 1) {
          clearAll(); // also exits edit mode
        } else {
          dispatch({
            type: "deleteTrees",
            payload: graphics.map((x: Graphic) => x.attributes.id),
          });
        }
      }
    );
    return () => {
      if (deleteEventListener.current) deleteEventListener.current.remove();
    };
  }, [sketchVm, deleteEventListener, dispatch, state.features, clearAll]);

  // Remember last map mode
  useEffect(() => {
    if (mapModeRef.current !== mapMode) {
      mapModeRef.current = mapMode;
    }
  }, [mapMode, mapModeRef]);

  // Clean up when map mode is changed externally
  useEffect(() => {
    if (mapMode !== mapAdditionalModes.edit) {
      clearTimeout(instructionsResetTimeout.current);
      if (!Object.keys(featureTypes).includes(mapMode)) {
        setMapMessage(undefined);
      }
    }
  }, [mapMode, setMapMessage]);

  // Clear map mode if needed on unmount
  useEffect(() => {
    return () => {
      if (mapModeRef.current === mapAdditionalModes.edit) {
        setMapMode(undefined);
        setMapMessage(undefined);
        sketchVm.updateOnGraphicClick = false;
        clearTimeout(instructionsResetTimeout.current);
      }
    };
  }, [setMapMode, setMapMessage, sketchVm]);

  const sourcesCount = getFeatureSourcesCount(state.features);
  const featuresList = state.patchesList
    .concat(state.treesList)
    .concat(state.revegetationAreasList);

  const renderPatches = (source: featureSource) => {
    const list = state.patchesList.filter(
      (x: string) => state.features[x].source === source
    );
    if (!list.length) return null;
    return (
      <TypeSection typeName="Patches">
        <CompactTable
          headers={patchHeaders}
          headerStyles={patchHeaderStyles}
          rows={list.map((x: any) =>
            getPolygonFeatureRow(x, featureTypes.patch)
          )}
        />
      </TypeSection>
    );
  };

  const renderTrees = (
    source: featureSource,
    type: treeType.Patch | treeType.Scattered
  ) => {
    const treesList = state.treesList.filter((x: string) => {
      const tree = state.features[x] as Tree;
      return tree.type === type && tree.source === source;
    });
    if (!treesList.length) return null;
    return (
      <TypeSection typeName={type + " Trees"}>
        <CompactTable
          headers={treeHeaders}
          headerStyles={treeHeaderStyles}
          rows={treesList.map((x: any) => getTreeRow(x))}
        />
      </TypeSection>
    );
  };

  const renderRevegetationAreas = (source: featureSource) => {
    const list = state.revegetationAreasList.filter(
      (x: string) => state.features[x].source === source
    );
    if (!list.length) return null;
    return (
      <TypeSection typeName="Revegetation areas">
        <CompactTable
          headers={revegetationAreaHeaders}
          headerStyles={revegetationAreaHeaderStyles}
          rows={list.map((x: any) =>
            getPolygonFeatureRow(x, featureTypes.revegetationArea)
          )}
        />
      </TypeSection>
    );
  };

  if (!featuresList.length) {
    return <Container />;
  }
  return (
    <Container>
      <FeaturesListContainer>
        <ControlsContainer>
          <Switch
            id="editSwitch"
            onChange={onEditSwitchChange}
            checked={mapMode === mapAdditionalModes.edit}
            labelUnchecked="Edit mode off"
            labelChecked="Edit mode on"
          />
          <ControlButton onClick={clearAll}>Clear all</ControlButton>
        </ControlsContainer>
        <FlaggedTreesWarningContainer>
          {anyFlaggedTrees && (
            <Callout type="warning">
              Please select the appropriate large tree benchmark for the flagged
              trees below
            </Callout>
          )}
        </FlaggedTreesWarningContainer>
        <SourcesContainer>
          {[featureSource.Drawn, featureSource.Uploaded].map((source) => {
            return (
              <SourceSection
                key={source}
                source={source}
                showHeader={sourcesCount === 2}
              >
                {renderPatches(source)}
                {renderTrees(source, treeType.Patch)}
                {renderTrees(source, treeType.Scattered)}
                {renderRevegetationAreas(source)}
              </SourceSection>
            );
          })}
        </SourcesContainer>
      </FeaturesListContainer>
    </Container>
  );
}

export default FeaturesList;
